<template>
  <div class="max-width">
    <div class="themes-page">
      <div class="subs">
        <router-link to="/" class="subs__links">Home</router-link>
        <span>Themes</span>
      </div>

      <div class="section__head section__head_middle">
        <div class="section__subheading fadeIn">Choose</div>
        <h1 class="section__title">a Perfect Theme That Sells</h1>
        <div class="section__text">Themes that combine stunning design with powerful conversion tools to maximize revenue and enhance the&nbsp;shopping journey.</div>
      </div>

      <div class="themes-page__tags">
        <button
            type="button"
            class="themes-page__tags-item"
            :class="{ 'themes-page__tags-item_active': selectedTag === 'all' }"
            @click="selectedTag = 'all'"
        >
          All Themes
        </button>
        <button
            v-for="tag in availableTags"
            :key="tag"
            type="button"
            class="themes-page__tags-item"
            :class="{ 'themes-page__tags-item_active': selectedTag === tag }"
            @click="selectedTag = tag"
        >
          {{ capitalize(tag) }}
        </button>
      </div>

      <div class="themes-page__grid">
        <ThemeCard
            v-for="theme in filteredThemes"
            :key="theme.title"
            :theme-link="theme.themeLink"
            :theme-name="theme.themeName"
            :title="theme.title"
            :preview-link="theme.previewLink"
            :tag="theme.tag"
            :image="theme.image"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import ThemeCard from '@/components/ThemeCard.vue'

import { useHead } from '@vueuse/head'

useHead({
  title: 'Themes – Almost Perfect Studio',
  meta: [
    { name: 'description', content: 'Explore bold, modern Shopify themes built for high performance with sales-driven features. Choose a theme that sells.' }
  ]
})

const themes = ref([])
const selectedTag = ref('all')
const sortOrder = ref('popularity')

const listId = 'all-themes'

onMounted(async () => {
  const res = await fetch('/assets/json/themes.json')
  const data = await res.json()

  themes.value = data.flatMap(theme =>
      theme.styles
          .filter(style => !(style.hiddenIn?.includes(listId) || theme.hiddenIn?.includes(listId)))
          .map(style => ({
            ...style,
            themeLink: `/themes/${theme.id}`,
            themeName: theme.id
          }))
  )
})

const availableTags = computed(() => {
  const tagsSet = new Set(themes.value.map(theme => theme.tag.toLowerCase()))
  return Array.from(tagsSet)
})

const filteredThemes = computed(() => {
  let filtered = themes.value

  if (selectedTag.value !== 'all') {
    filtered = filtered.filter(theme => theme.tag.toLowerCase() === selectedTag.value)
  }

  if (sortOrder.value === 'name') {
    filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title))
  }

  if (sortOrder.value === 'latest') {
    filtered = themes.value
  }

  return filtered
})

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)
</script>

<style lang="scss">
.themes-page {

  .section__head {
    max-width:730px;
    margin-bottom:2.5rem;
  }

  &__tags {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 6px;
    margin: 0 auto 4.5rem;
    max-width: 782px;

    @include md {
      margin-bottom:2.5rem;
    }

    &-item {
      cursor: pointer;
      appearance: none;
      border: 0;
      background: var(--color-gray-light);
      font-family: var(--font-family);
      color: var(--color-accent);
      height: 1.875rem;
      display: flex;
      text-transform:capitalize;
      align-items: center;
      padding: 0 .75rem;
      font-size: 1rem;
      border-radius: 8px;
      transition: var(--transition);

      @include md {
        font-size:.875rem;
      }

      &_active,
      &:hover {
        background: var(--color-accent);
        color: #fff;
      }
    }
  }

  &__sort {
    text-align:right;
    font-size:.875rem;
    margin-bottom: 1.25rem;
    letter-spacing: -0.01em;

    select {
      font-size:.875rem;
      color:var(--color-accent);
      border:0;
      background:none;
      font-family:var(--font-family);
      cursor:pointer;
      outline:none;
      user-select:none;
      font-weight:500;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    grid-row-gap: 3rem;

    @include sm {
      grid-template-columns: repeat(1, 1fr);
    }
  }
}
</style>
